import Grid from 'Geodata/grid';
import FVector from '@data/geometry/FVector';
import type { CoordinateSystemSelection } from '@visualizer/types';

export type Coordinate = { x: number; y: number; z: number };

export type CoordinateSystem = {
    origin: Coordinate; // Origin of the coordinate system in national coordinates (e.g., LV95)
    angleCcw: number; // CCW angle in degrees
};

export type StoredCoordinateSystem = 'geo' | 'local';

function hasMeaningfulCustomSelection(selection?: Partial<CoordinateSystemSelection> | null): boolean {
    if (!selection) return false;

    return (
        selection.centerX !== undefined &&
        selection.centerY !== undefined &&
        selection.centerZ !== undefined &&
        (selection.centerX !== 0 || selection.centerY !== 0 || selection.centerZ !== 0 || (selection.alpha ?? 0) !== 0)
    );
}

export function resolveCoordinateSystemSelection(selection?: Partial<CoordinateSystemSelection> | null): CoordinateSystemSelection {
    const national = getNationalCoordinateSystem();
    const mode = selection?.mode ?? 'lv95';
    const preservedCustomSelection = hasMeaningfulCustomSelection(selection) ? selection : null;

    if (mode === 'lv95') {
        return {
            mode,
            centerX: preservedCustomSelection?.centerX ?? national.origin.x,
            centerY: preservedCustomSelection?.centerY ?? national.origin.y,
            centerZ: preservedCustomSelection?.centerZ ?? national.origin.z,
            alpha: preservedCustomSelection?.alpha ?? 0,
        };
    }

    return {
        mode,
        centerX: selection?.centerX ?? national.origin.x,
        centerY: selection?.centerY ?? national.origin.y,
        centerZ: selection?.centerZ ?? national.origin.z,
        alpha: selection?.alpha ?? 0,
    };
}

function getNationalCoordinateSystem(): CoordinateSystem {
    return {
        origin: Grid.gridOrigin,
        angleCcw: 0,
    };
}

function getCustomCoordinateSystem(selection: CoordinateSystemSelection): CoordinateSystem {
    return {
        origin: {
            x: selection.centerX,
            y: selection.centerY,
            z: selection.centerZ,
        },
        angleCcw: selection.alpha,
    };
}

export function getCurrentCoordinateSystem(selection?: Partial<CoordinateSystemSelection> | null): CoordinateSystem {
    const resolved = resolveCoordinateSystemSelection(selection);

    return resolved.mode === 'custom' ? getCustomCoordinateSystem(resolved) : getNationalCoordinateSystem();
}

function isCustomCoordinateSystem(selection?: Partial<CoordinateSystemSelection> | null): boolean {
    return resolveCoordinateSystemSelection(selection).mode === 'custom';
}

function toRadians(degrees: number): number {
    return (degrees * Math.PI) / 180;
}

function rotateHorizontal(x: number, y: number, angleCcw: number): { x: number; y: number } {
    const angleRad = toRadians(angleCcw);
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);

    return {
        x: x * cos - y * sin,
        y: x * sin + y * cos,
    };
}

// =====================================
// COORDINATE TRANSFORMATION
// =====================================

// Maps from scene-local (UM) Coordinates coordinates to currently selected CCRS (either National or Custom):
export function toCurrentCoordinate(local: Coordinate, selection?: Partial<CoordinateSystemSelection> | null): FVector {
    const national = getNationalCoordinateSystem();

    // 1. Add scene-local coords to the Origin of the National Coordinate System:
    const worldX = national.origin.x + local.x;
    const worldY = national.origin.y + local.y;
    const worldZ = national.origin.z + local.z;

    // 1.5. Return National Coordinates:
    if (!isCustomCoordinateSystem(selection)) {
        return new FVector(worldX, worldY, worldZ);
    }

    // 2. Get the CCRS, translate and then rotate National Coordinates:
    const coordinateSystem = getCurrentCoordinateSystem(selection);
    const translatedX = worldX - coordinateSystem.origin.x;
    const translatedY = worldY - coordinateSystem.origin.y;
    const translatedZ = worldZ - coordinateSystem.origin.z; // Z not affected by hor. rot.
    const rotated = rotateHorizontal(translatedX, translatedY, -coordinateSystem.angleCcw);

    // 3. Return the Custom Coordinates (with origin in the CCRS and rotated by alpha):
    return new FVector(rotated.x, rotated.y, translatedZ);
}

// Maps from the currently selected coordinate system back to scene-local (UM) coordinates:
export function fromCurrentCoordinate(point: Coordinate, selection?: Partial<CoordinateSystemSelection> | null): FVector {
    const national = getNationalCoordinateSystem();

    // 1. Subtract the Origin of the National Coordinate System to obtain scne-local coords:
    if (!isCustomCoordinateSystem(selection)) {
        return new FVector(point.x - national.origin.x, point.y - national.origin.y, point.z - national.origin.z);
    }

    // 2. Get the CCRS, unrotate and untranslate to go back to the National Coordinate System:
    const coordinateSystem = getCurrentCoordinateSystem(selection);
    const rotated = rotateHorizontal(point.x, point.y, coordinateSystem.angleCcw);
    const worldX = coordinateSystem.origin.x + rotated.x;
    const worldY = coordinateSystem.origin.y + rotated.y;
    const worldZ = coordinateSystem.origin.z + point.z;

    // 3. Subtract the Origin of the National Coordinate System to obtain scene-local coords:
    return new FVector(worldX - national.origin.x, worldY - national.origin.y, worldZ - national.origin.z);
}

// =====================================
// CAMERA UTILS
// =====================================

// Camera serialization still uses the legacy scene-axis convention: x, -z, y.
export function toCurrentSceneCoordinate(local: Coordinate, selection?: Partial<CoordinateSystemSelection> | null): FVector {
    return toCurrentCoordinate({ x: local.x, y: -local.z, z: local.y }, selection);
}

export function fromCurrentSceneCoordinate(point: Coordinate, selection?: Partial<CoordinateSystemSelection> | null): FVector {
    const standardCoordinate = fromCurrentCoordinate(point, selection);
    return new FVector(standardCoordinate.x, standardCoordinate.z, -standardCoordinate.y);
}

// Detect whether a stored camera uses local scene units or geospatial coordinates:
// This was here before the concept of custom coordinate system, might not be necessary, but need to check.
export function inferStoredCoordinateSystem(position: Coordinate, target: Coordinate): StoredCoordinateSystem {
    const maxAbsStored = Math.max(
        Math.abs(position.x),
        Math.abs(position.y),
        Math.abs(position.z),
        Math.abs(target.x),
        Math.abs(target.y),
        Math.abs(target.z)
    );

    // Legacy local cameras are usually near scene origin; national coordinates are much larger:
    return maxAbsStored > 40000 ? 'geo' : 'local';
}
